/* eslint-disable max-lines */

import {
	FETCH_SUBSCRIPTION, UPDATE_SUBSCRIPTION, UPDATE_SHIPPING_ADDRESS, CHANGE_NEXT_BILLING_DATE,
	LIST_SCHEDULED_ORDERS, LIST_PAYMENT_METHODS, CHANGE_PAYMENT_METHOD,
	UPDATE_DELIVERY_FREQUENCY
} from "utils/action-types";
import { loopTokenKey } from "utils/constants";
import mapSubscriptionItems from "helpers/map-subscription-items";
import { storefrontApiCall, getLatestSubscription, withLoopToken } from "helpers/loop";
import { fetchCustomerOrders } from "./profile";
import { findClosestBox } from "utils/cart";

export const fetchSubscriptionById = (id) => async (dispatch, getState) => {
	const { subscriptionId = id } = getState().account.subscription;
	const response = await storefrontApiCall({ method: "get", endpoint: `/subscription/${subscriptionId}` });

	if (response?.success) {
		const { deliveryPrice, deliveryMethod } = response.data || {};
		await dispatch({ type: FETCH_SUBSCRIPTION, payload: { deliveryPrice, deliveryMethod } });
		return response.data;
	}

	return {};
};

export const fetchSubscriptions = () => async (dispatch, getState) => {
	const { cartBoxes, commitmentBasedDiscounts } = getState().global;
	const response = await storefrontApiCall({ method: "get", endpoint: "/subscription" });

	if (response.success) {
		const subscription = response.data.length ? getLatestSubscription(response.data) : null;
		const matchedDiscount = subscription?.discounts.length > 0 ? commitmentBasedDiscounts.find(commitmentBased =>
			subscription?.discounts.some(discount => discount.title === commitmentBased.title)
		) : null;
		let payload = {};
		if (subscription) {
			const lines = mapSubscriptionItems(subscription.lines);
			const totalWeight = Object.values(lines).reduce((acc, item) => {
				if (item.weightInGrams === 1) return acc;
				else return acc + item.totalQuantity * item.weightInGrams / 1000;
			}, 0);

			payload = {
				...subscription,
				isActive: subscription.status === "ACTIVE",
				isCancelled: subscription.status === "CANCELLED",
				subscriptionId: subscription.id,
				activeBox: findClosestBox(cartBoxes, totalWeight),
				totalWeight,
				lines,
				commitmentBasedDiscount: matchedDiscount,
			};
		}

		dispatch({ type: FETCH_SUBSCRIPTION, payload });
		return payload;
	} else {
		return null;
	}
};

export const updateDeliveryFrequency = (billingPolicy, nextBillingDateEpoch, cancellationFlowId) =>
	async (dispatch, getState) => {
		const { interval, intervalCount } = billingPolicy;
		const { subscriptionId } = getState().account.subscription;
		const data = { billingPolicy: { interval, intervalCount }, nextBillingDateEpoch };

		if (cancellationFlowId)
			data.cancellationFlowId = cancellationFlowId;

		const response = await storefrontApiCall({ method: "put", endpoint: `/subscription/${subscriptionId}/frequency`, data });

		if (response?.success) {
			dispatch({ type: UPDATE_DELIVERY_FREQUENCY, payload: billingPolicy });
			dispatch(fetchScheduledOrders());
		}

		return response;
	};

export const reactivateSubscription = () => async (dispatch, getState) => {
	const { subscriptionId, status } = getState().account.subscription;
	/* Reactivate is used when the subscription is cancelled and needs to be reactivated.
	Resume is used when the subscription is paused and needs to be resumed. */
	const method = status === "PAUSED" ? "resume" : "reactivate";
	const response = await storefrontApiCall({ endpoint: `/subscription/${subscriptionId}/${method}` });

	if (response?.success)
		dispatch({ type: UPDATE_SUBSCRIPTION, payload: response.data });

	return response;
};

export const cancelSubscription = (cancellationFlowId, cancellationOfferId) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({
		endpoint: `/subscription/${subscriptionId}/cancel`, data: { cancellationFlowId, cancellationOfferId }
	});

	if (response?.success)
		dispatch({ type: UPDATE_SUBSCRIPTION, payload: response.data });

	return response;
};

export const pauseSubscription = (intervalCount, intervalType, cancellationFlowId) => async (dispatch, getState) => {
	const { subscriptionId, scheduledOrders } = getState().account.subscription;
	const data = {};
	if (intervalCount && intervalType)
		data.pauseDuration = { intervalCount, intervalType };

	if (cancellationFlowId)
		data.cancellationFlowId = cancellationFlowId;

	const response = await storefrontApiCall({ endpoint: `/subscription/${subscriptionId}/pause`, data });

	if (response?.success) {
		dispatch({ type: UPDATE_SUBSCRIPTION, payload: response.data });
		if (intervalCount && Object.keys(scheduledOrders).length)
			dispatch(fetchScheduledOrders());
	}

	return response;
};

export const fetchFlowRewardBanner = () => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({ method: "get", endpoint: `/subscription/${subscriptionId}/flowRewardBanner` });

	if (response?.success && response.data.length > 0)
		return response.data[0];

	return {};
};

export const updateShippingAddress = (newAddress) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({
		method: "put",
		endpoint: `/subscription/${subscriptionId}/address`,
		data: newAddress
	});

	if (response?.success) {
		dispatch({ type: UPDATE_SHIPPING_ADDRESS, payload: response.data });
		dispatch(updateLocationIdAttribute(newAddress.zip));
	}

	return response;
};

export const fetchCancellationOffers = (cancellationFlowId) => async (_, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({
		method: "get", endpoint: `/subscription/${subscriptionId}/cancellationOffer?cancellationFlowId=${cancellationFlowId}`
	});

	return response;
};

export const applyCancellationOffer = (cancellationFlowId, cancellationOfferId) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({
		endpoint: `/subscription/${subscriptionId}/cancellationOffer`,
		data: { cancellationFlowId, cancellationOfferId }
	});

	if (response?.success)
		dispatch(fetchSubscriptions());

	return response;
};

export const applyDiscountCode = (code) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;

	return withLoopToken(async (auth) => {
		const response = await fetch("/.netlify/functions/add-discount-code", {
			method: "POST",
			body: JSON.stringify({ code, subscriptionId, accessToken: auth.accessToken }),
		});

		const data = await response.json();

		if (data?.success) {
			await dispatch(fetchSubscriptions());
			const { subscription } = getState().account;
			const discount = subscription?.discounts.find(discount => discount.title === code);
			return { ...data, discount };
		}

		return data;
	});
};

export const removeDiscount = (discountId, refreshSubscription = true) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const data = await storefrontApiCall({
		method: "delete",
		endpoint: `/subscription/${subscriptionId}/discount/${discountId}`,
	});

	if (data?.success && refreshSubscription)
		await dispatch(fetchSubscriptions());

	return data;
};

export const listPaymentMethods = () => async (dispatch) => {
	const response = await storefrontApiCall({ method: "get", endpoint: "/paymentMethod" });

	if (response?.success)
		await dispatch({ type: LIST_PAYMENT_METHODS, payload: response.data });

	return response;
};

export const changePaymentMethod = (paymentMethodId) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;

	const data = await storefrontApiCall(
		{
			method: "put",
			endpoint: `/subscription/${subscriptionId}/paymentMethod`,
			data: { paymentMethodId }
		});

	if (data?.success)
		await dispatch({
			type: CHANGE_PAYMENT_METHOD,
			payload: { paymentMethodId }
		});

	return data;
};

export const updatePaymentMethod = (paymentMethodId) =>
	storefrontApiCall({ endpoint: `/paymentMethod/${paymentMethodId}/update` });

let fetchSubscriptionTimeout;

export const bulkUpdateLines = (shouldUpdateShippingMethod) =>
	async (dispatch, getState) => {
		const { subscriptionId, lines, draftChanges } = getState().account.subscription;

		try {
			const response = await fetch("/.netlify/functions/bulk-line-update", {
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${JSON.parse(localStorage.getItem(loopTokenKey)).accessToken}`
				},
				method: "POST",
				body: JSON.stringify({
					lines: Object.values(lines).map(({ weightInGrams, quantity, totalQuantity, name }) =>
						({ weightInGrams, quantity: totalQuantity || quantity, name })),
					subscriptionId,
					linesToAdd: Object.values(draftChanges.linesToCreate).map(
						({ variantShopifyId, quantity, isUpsell, customAttributes }) =>
							({ variantShopifyId, quantity, isUpsell, customAttributes })),
					linesToRemove: Object.keys(draftChanges.linesToRemove).map((id) => ({ lineId: +id })),
					linesToUpdate: Object.values(draftChanges.linesToUpdate).map(({ id: lineId, quantity }) => ({ lineId, quantity }))
				})
			});
			const data = await response.json();
			let subscription;

			if (data?.success) {
				subscription = await dispatch(fetchSubscriptions());

				//  No need to call fetchSubscription again for store that has the shipping methods
				// since it's already triggered by updateShippingMethod
				if (!shouldUpdateShippingMethod) {
					if (fetchSubscriptionTimeout)
						clearTimeout(fetchSubscriptionTimeout);

					fetchSubscriptionTimeout = setTimeout(() => dispatch(fetchSubscriptionById()), 1000);
				}
				return { ...response, subscription, success: true };
			} else {
				return { success: false, message: "Bulk update failed" };
			}
		} catch (err) {
			console.error({ error: err });
			return { success: false, subscription: {} };
		}
	};

export const addLineOnce = (variantShopifyId) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;

	const data = await storefrontApiCall({
		method: "post",
		endpoint: `/subscription/${subscriptionId}/line/once`,
		data: {
			variantShopifyId: variantShopifyId,
			quantity: 1
		}
	});

	if (data?.success)
		await dispatch(fetchSubscriptions());

	return data;
};

export const fetchScheduledOrders = () => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({
		method: "get",
		endpoint: `/subscription/${subscriptionId}/order/schedule`,
	});

	if (response?.success && response.data?.length) {
		dispatch({
			type: LIST_SCHEDULED_ORDERS,
			payload: { orders: response.data }
		});
	}
};

export const updateShippingMethod = (deliveryPrice, deliveryMethodTitle, isLocalDelivery) =>
	async (dispatch, getState) => {
		const { subscriptionId } = getState().account.subscription;

		const response = await fetch("/.netlify/functions/update-shipping-method", {
			method: "POST",
			body: JSON.stringify({ deliveryPrice, deliveryMethodTitle, subscriptionId, isLocalDelivery }),
		})
			.then((res) => res.json());

		if (response?.success)
			await dispatch(fetchSubscriptionById());

		return response;
	};

export const rescheduleOrder = (newBillingDateEpoch, placeOrder = false) => async (dispatch, getState) => {
	const { subscriptionId } = getState().account.subscription;
	const response = await storefrontApiCall({
		endpoint: `/subscription/${subscriptionId}/${placeOrder ? "placeOrder" : "reschedule"}`,
		data: { newBillingDateEpoch }
	});

	if (response?.success) {
		dispatch({ type: CHANGE_NEXT_BILLING_DATE, payload: response.data });
		await dispatch(fetchScheduledOrders());

		if (placeOrder) {
			let subscription;
			// Polling until hasOrdersInProcessing is false
			for (; ;) {
				subscription = await dispatch(fetchSubscriptionById());
				if (!subscription?.hasOrdersInProcessing) {
					await Promise.all([
						dispatch(fetchScheduledOrders()),
						dispatch(fetchSubscriptions())
					]);
					// Fetch orders again to ensure the latest order's delivery date is blocked from rescheduling calendar
					dispatch(fetchCustomerOrders());
					break;
				}
				// Wait 1 second before checking again
				await new Promise(resolve => setTimeout(resolve, 1000));
			}
		}
	}

	return response;
};

export const updateLocationIdAttribute = (countryCode) => async (dispatch, getState) => {
	const { subscriptionId, attributes } = getState().account.subscription;

	try {
		const response = await fetch("/.netlify/functions/update-location-id-attribute", {
			method: "PUT",
			body: JSON.stringify({ subscriptionId, attributes, countryCode })
		});

		const data = await response.json();

		if (response.status === 400)
			return { error: data?.message || "Invalid request data" };

		if (data?.success)
			await dispatch(fetchSubscriptions());

		return data;
	} catch (error) {
		console.error("Error updating location:", error.message);
		return { error: error.message };
	}
};